import { apiDelete, apiGet, apiPost, apiPut } from '../../../adapters/api-client/api';

/**
 * The operations console's own read endpoints — Sprint 1, UC-A13.
 *
 * Only what the console chrome needs today. The queue, audit and parameter
 * screens get their own client as each is built.
 */

export type SearchSubject =
  | 'case'
  | 'audit'
  | 'teacher'
  | 'family'
  | 'learner'
  | 'course'
  | 'enrollment'
  | 'session'
  | 'transaction';

export interface OpsSearchHit {
  subject: SearchSubject;
  id: string;
  title: string;
  detail: string;
  route: string;
  /** True when the query was this record's identifier, exactly. */
  exactId: boolean;
  /**
   * True when the preview was narrowed because of who is looking.
   *
   * Surfaced rather than hidden: an operator who sees a shortened learner name
   * should know it was shortened, or they will think the record is incomplete
   * and go looking for a fuller one that does not exist.
   */
  redacted: boolean;
}

export interface OpsSearchResponse {
  query: string;
  results: OpsSearchHit[];
  /** Stores that could not be read. An incomplete search says so. */
  gaps: string[];
  /** DEC-A13-03: an exact id with nothing this operator may see. */
  code?: 'NOT_FOUND_OR_NOT_AUTHORIZED';
}

export function searchOperations(query: string): Promise<OpsSearchResponse> {
  return apiGet(`/api/v1/admin/search?q=${encodeURIComponent(query)}`);
}

/* ------------------------------------------------------- the case queue */

export type CaseSlaState = 'BREACHED' | 'DUE_SOON' | 'SCHEDULED';
export type CaseSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
export type CaseStatus = 'OPEN_UNASSIGNED' | 'IN_PROGRESS' | 'BLOCKED' | 'ESCALATED' | 'CLOSED';

export interface CaseEscalation {
  toQueue: string;
  reason: string;
  note?: string;
  escalatedBy: string;
  escalatedAt: string;
  /** DEC-A02-14: the target could not take it. */
  targetUnavailable?: boolean;
  fallbackQueue?: string;
}

export interface OpsCase {
  caseId: string;
  caseType: string;
  sourceType: string;
  sourceId: string;
  sourceVersion: string;
  module: string;
  /** Decided by the source. The console shows it and never moves it. */
  severity: CaseSeverity;
  status: CaseStatus;
  title: string;
  subtitle: string;
  family?: string;
  amountUsd?: number;
  createdAt: string;
  openedAt?: string;
  slaHours?: number;
  slaPolicyVersion?: string;
  deadlineAt?: string;
  /** No `hoursRemaining` means this case has no deadline. */
  sla: { state: CaseSlaState; dueAt?: string; hoursRemaining?: number; dueSoonHours: number };
  /** Why this matters, written for a trained operator. */
  why: string;
  /** What to do, in order. */
  nextActions: string[];
  playbook?: { id: string; version: number };
  sourceRoute: string;
  /** The version every ownership command presents — DEC-A02-09. */
  ownerVersion: number;
  owner?: { operatorId: string; claimedAt?: string };
  escalation?: CaseEscalation;
  blockReason?: string;
  nextCheckAt?: string;
  flagged: boolean;
  noteCount: number;
  /** The source could not be read just now; this is the stored record alone. */
  sourceUnavailable?: boolean;
}

export interface OpsQueue {
  cases: OpsCase[];
  critical: OpsCase[];
  breaching: OpsCase[];
  dueSoon: OpsCase[];
  scheduled: OpsCase[];
  /** Rails that could not be read. An incomplete queue says so. */
  gaps: Array<{ module: string; reason: string }>;
  /** The governed window the blocks were cut with — DEC-A02-07. */
  dueSoonWindowHours: number;
  /** The closed lists, served so the console holds no second copy. */
  escalationReasons: Array<{ value: string; label: string }>;
  escalationTargets: Array<{ value: string; label: string }>;
  closeReasons: string[];
  /** Registered types nothing can produce yet. */
  caseTypesWithoutSource: string[];
}

export interface CaseNote {
  noteId: string;
  caseId: string;
  authorId: string;
  body: string;
  createdAt: string;
}

export interface HandoverDraft {
  draft: string;
  generatedAt: string;
  caseRefs: Array<{ caseId: string }>;
}

const casePath = (caseId: string) => `/api/v1/admin/cases/${encodeURIComponent(caseId)}`;

export const getOpsQueue = (): Promise<OpsQueue> => apiGet('/api/v1/admin/cases');

export const claimCase = (
  caseId: string,
  expectedOwnerVersion: number,
): Promise<{ claimedAt: string; ownerVersion: number }> =>
  apiPost(`${casePath(caseId)}/claim`, { expectedOwnerVersion });

export const releaseCase = (caseId: string, expectedOwnerVersion: number): Promise<void> =>
  apiDelete(`${casePath(caseId)}/claim?expectedOwnerVersion=${expectedOwnerVersion}`);

export const reassignCase = (
  caseId: string,
  input: { toOperatorId: string; reason: string; expectedOwnerVersion: number },
): Promise<{ ownerVersion: number }> => apiPost(`${casePath(caseId)}/reassign`, input);

export const blockCase = (
  caseId: string,
  input: { blockReason: string; nextCheckAt: string },
): Promise<void> => apiPost(`${casePath(caseId)}/block`, input);

export const resumeCase = (caseId: string): Promise<void> =>
  apiPost(`${casePath(caseId)}/resume`, {});

export const closeCase = (
  caseId: string,
  input: { closeReason: string; note: string },
): Promise<void> => apiPost(`${casePath(caseId)}/close`, input);

export const flagCase = (caseId: string, flagged: boolean): Promise<void> =>
  apiPut(`${casePath(caseId)}/flag`, { flagged });

export const getCaseNotes = (caseId: string): Promise<{ notes: CaseNote[] }> =>
  apiGet(`${casePath(caseId)}/notes`);

export const addCaseNote = (caseId: string, body: string): Promise<CaseNote> =>
  apiPost(`${casePath(caseId)}/notes`, { body });

export const escalateCase = (
  caseId: string,
  input: { reason: string; target: string; note: string },
): Promise<{ status: CaseStatus; toQueue: string; targetUnavailable: boolean }> =>
  apiPost(`${casePath(caseId)}/escalate`, input);

export const getHandoverDraft = (): Promise<HandoverDraft> =>
  apiGet('/api/v1/admin/cases/handover/draft');

export const sendHandover = (input: {
  note: string;
  toShift: string;
  generatedAt: string;
  draftCaseIds: string[];
}): Promise<{ handoverId: string; addedAtFinalize: string[]; resolvedSinceDraft: string[] }> =>
  apiPost('/api/v1/admin/cases/handover', input);

/* ----------------------------------------------------------- audit log */

export interface AuditEntry {
  auditId: string;
  occurredAt: string;
  occurredOn: string;
  action: string;
  actorId: string;
  actorLabel: string;
  objectType: string;
  objectId: string;
  objectLabel: string;
  reason: string;
  reference: string;
  correlationId: string;
  permissionExercised?: string;
  parameterVersion?: string;
  highRisk: boolean;
  expiresAt?: number;
  legalHold?: { placedBy: string; placedAt: string; reason: string };
}

export const searchAudit = (params: {
  day?: string;
  from?: string;
  to?: string;
  action?: string;
}): Promise<{ entries: AuditEntry[] }> => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && query.set(k, v));
  return apiGet(`/api/v1/admin/audit?${query.toString()}`);
};

/* ------------------------------------------------------------ playbooks */

export interface Playbook {
  id: string;
  version: number;
  title: string;
  module: string;
  category: string;
  steps: Array<{ number: number; instruction: string }>;
  standingRule: string;
}

export const getPlaybooks = (): Promise<{ playbooks: Playbook[] }> =>
  apiGet('/api/v1/admin/playbooks');

/* ----------------------------------------------------------- parameters */

export interface ParameterView {
  definition: {
    key: string;
    name: string;
    unit: string;
    defaultValue: number;
    /** Absent when the deciding document states no bounds. */
    min?: number;
    max?: number;
    step: number;
    /** Decimal places the value may carry; absent means a whole number. */
    decimals?: number;
    description: string;
    source: string;
    group: string;
    /** ADM-D01: true when a change needs two distinct approvers. */
    sensitive: boolean;
    impactClass: string;
  };
  value: number;
  version?: number;
  /** The highest version written; sent back as `expectedVersion` (DEC-A13-14). */
  latestVersion: number;
  effectiveFrom?: string;
  movedFromDefault: boolean;
  pending?: { value: number; effectiveFrom: string; version: number };
}

export const getParameters = (): Promise<{ parameters: ParameterView[] }> =>
  apiGet('/api/v1/admin/params');

export const changeParameter = (
  key: string,
  input: { value: number; reason: string; expectedVersion: number },
): Promise<{ state: 'ACTIVE' | 'PROPOSED' }> =>
  apiPut(`/api/v1/admin/params/${encodeURIComponent(key)}`, input);

/** A high-impact change waiting on its two approvers (ADM-D01). */
export interface ParameterProposal {
  proposalId: string;
  key: string;
  value: number;
  reason: string;
  requestedBy: string;
  approvals: { slot: 'DOMAIN' | 'CONTROL'; operatorId: string }[];
}

export const getParameterProposals = (): Promise<{ proposals: ParameterProposal[] }> =>
  apiGet('/api/v1/admin/params/proposals');

export const approveParameterProposal = (
  key: string,
  proposalId: string,
): Promise<{ state: 'ACTIVE' | 'PROPOSED' }> =>
  apiPost(
    `/api/v1/admin/params/${encodeURIComponent(key)}/proposals/${encodeURIComponent(
      proposalId,
    )}/approve`,
    {},
  );

export const rejectParameterProposal = (
  key: string,
  proposalId: string,
  reason: string,
): Promise<unknown> =>
  apiPost(
    `/api/v1/admin/params/${encodeURIComponent(key)}/proposals/${encodeURIComponent(
      proposalId,
    )}/reject`,
    { reason },
  );
